/**
 * Дописывает параметры в адрес, не трогая остальное.
 *
 * Адреса из API непрозрачны: в них может прийти идентичность посетителя
 * (`_ac_cid`, `_ac_ref`) и что угодно ещё. Поэтому путь, hash и чужие пары query
 * остаются байт в байт — кодируются только наши значения. Склейка строк
 * (`${url}?${query}`) давала второй `?` и портила последний параметр адреса.
 */
export type QueryValue = string | number | boolean | null | undefined
export type QueryInput = string | URLSearchParams | Record<string, QueryValue | QueryValue[]>

function toSearchParams (params: QueryInput): URLSearchParams {
  if (typeof params === 'string' || params instanceof URLSearchParams) {
    return new URLSearchParams(params)
  }
  const result = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    const values = Array.isArray(value) ? value : [value]
    values
      .filter(item => item !== null && item !== undefined)
      .forEach(item => result.append(key, String(item)))
  })
  return result
}

function decodeKey (pair: string): string {
  const rawKey = pair.split('=')[0]
  try {
    return decodeURIComponent(rawKey.replace(/\+/g, ' '))
  } catch {
    return rawKey
  }
}

export function withQueryParams (
  url: string,
  params: QueryInput,
  options: { keepExisting?: boolean } = {}
): string {
  const ours = toSearchParams(params)
  if (!ours.toString()) {
    return url
  }

  const hashIndex = url.indexOf('#')
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex)
  const beforeHash = hashIndex === -1 ? url : url.slice(0, hashIndex)
  const queryIndex = beforeHash.indexOf('?')
  const path = queryIndex === -1 ? beforeHash : beforeHash.slice(0, queryIndex)
  const query = queryIndex === -1 ? '' : beforeHash.slice(queryIndex + 1)

  // Хвостовые `&` (и пустой query) не дают пустых пар; пустые пары внутри — как пришли.
  const existing = query.split('&')
  while (existing.length && !existing[existing.length - 1]) {
    existing.pop()
  }
  const existingKeys = new Set(existing.map(decodeKey))

  if (options.keepExisting) {
    existingKeys.forEach(key => ours.delete(key))
    if (!ours.toString()) {
      return url
    }
  }
  const ourKeys = new Set(Array.from(ours.keys()))
  const kept = options.keepExisting ? existing : existing.filter(pair => !ourKeys.has(decodeKey(pair)))

  const merged = [...kept, ours.toString()].join('&')
  // eslint-disable-next-line no-restricted-syntax -- это и есть хелпер сборки query
  return `${path}${merged ? `?${merged}` : ''}${hash}`
}

/**
 * Убирает из адреса названные параметры. Остальное — путь, hash и прочие
 * пары query — байт в байт. Ключ сравнивается в раскодированном виде.
 */
export function withoutQueryParams (url: string, names: ReadonlyArray<string>): string {
  const hashIndex = url.indexOf('#')
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex)
  const beforeHash = hashIndex === -1 ? url : url.slice(0, hashIndex)
  const queryIndex = beforeHash.indexOf('?')
  if (queryIndex === -1) {
    return url
  }

  const removed = new Set(names)
  const pairs = beforeHash.slice(queryIndex + 1).split('&').filter(Boolean)
  const kept = pairs.filter(pair => !removed.has(decodeKey(pair)))
  if (kept.length === pairs.length) {
    return url
  }

  const path = beforeHash.slice(0, queryIndex)
  // eslint-disable-next-line no-restricted-syntax -- это и есть хелпер сборки query
  return `${path}${kept.length ? `?${kept.join('&')}` : ''}${hash}`
}

function decodeValue (raw: string): string | null {
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '))
  } catch {
    return null
  }
}

/** Сколько уровней вложенных адресов разбираем: `returnUrl` внутри `returnUrl` — уже экзотика. */
const NESTED_URL_DEPTH = 2

function withoutNestedParams (pair: string, names: ReadonlyArray<string>, depth: number): string {
  const eqIndex = pair.indexOf('=')
  const value = eqIndex === -1 ? null : decodeValue(pair.slice(eqIndex + 1))
  if (!value || !value.includes('?')) {
    return pair
  }
  const cleaned = withoutQueryParamsDeep(value, names, depth - 1)
  return cleaned === value ? pair : pair.slice(0, eqIndex + 1) + encodeURIComponent(cleaned)
}

/**
 * Как `withoutQueryParams`, но заглядывает и во вложенные адреса: query внутри
 * hash-роута (`#/course?token=`) и закодированный адрес в значении параметра
 * (`?returnUrl=%2Fp%3Ftoken%3D…`). Значение, из которого что-то вырезали,
 * кодируется заново; всё остальное — байт в байт.
 */
export function withoutQueryParamsDeep (
  url: string,
  names: ReadonlyArray<string>,
  depth: number = NESTED_URL_DEPTH
): string {
  const top = withoutQueryParams(url, names)
  if (depth <= 0) {
    return top
  }

  const hashIndex = top.indexOf('#')
  const beforeHash = hashIndex === -1 ? top : top.slice(0, hashIndex)
  const queryIndex = beforeHash.indexOf('?')
  const withCleanQuery = queryIndex === -1
    ? beforeHash
    : beforeHash.slice(0, queryIndex + 1) + beforeHash.slice(queryIndex + 1)
      .split('&')
      .map(pair => withoutNestedParams(pair, names, depth))
      .join('&')

  if (hashIndex === -1) {
    return withCleanQuery
  }
  const hash = top.slice(hashIndex + 1)
  return `${withCleanQuery}#${hash.includes('?') ? withoutQueryParamsDeep(hash, names, depth - 1) : hash}`
}

/**
 * Параметры, которые не должны попадать ни в аналитику, ни в href ссылок на
 * странице (счётчики читают href сами): токен входа и идентичность посетителя.
 */
export const SENSITIVE_URL_PARAMS: ReadonlyArray<string> = ['token', '_uah', 'deep_link_sub1', '_ac_cid', '_ac_ref']
